import { findRow, formatPrice } from "@/data/pricing";

const faqPrice = (name: string) => formatPrice(findRow(name)) ?? "Scoped on a call";

export type PageFaq = { q: string; a: string };

export const pageFaqAdditions: Record<string, PageFaq[]> = {
  "/fractional-general-manager": [
    {
      q: "Is a fractional GM the same as a fractional COO?",
      a: "Yes. Both describe a senior operator who holds the operating seat part time and runs the business beside the owner. Larger companies and the US market say COO; most Australian owner-led businesses say general manager. Ajility uses both names for the one service.",
    },
    {
      q: "What does a fractional general manager do in a small business?",
      a: "The fractional GM chairs the weekly leadership meeting, builds and runs the scorecard, holds managers to their quarterly priorities, fixes the processes that keep failing and keeps the owner out of day-to-day decisions. The work is delivery inside the business, not a report.",
    },
    {
      q: "Should I hire a full-time general manager or a fractional one?",
      a: "Hire full time once the seat is defined and there is five days of work in it. Use a fractional GM while the seat is still being defined, the rhythm is being built, or the business is under about $10M. Many owners do both: fractional first, then a full-time hire into a defined role.",
    },
    {
      q: "Can a fractional GM help me find and set up a full-time GM?",
      a: "Yes, except for the search itself. Ajility defines the seat, its numbers and decision rights, helps you judge candidates against them, and then sets the new GM up with the plan, scorecard and leadership rhythm over their first 90 days. The recruitment goes to a recruiter or your own process.",
    },
    {
      q: "How much does a fractional general manager cost in Australia?",
      a: `Ajility prices the seat as a monthly rhythm rather than a day rate: composed retainers start ${faqPrice("Fortnightly rhythm base")} for a fortnightly rhythm and ${faqPrice("Weekly rhythm base")} for a weekly rhythm, scoped to the size of the business and how much of the work is on site. Every price is published on the packages page.`,
    },
    {
      q: "Does Ajility work as a fractional GM in Perth and Western Australia?",
      a: "Yes. Ajility's principal currently holds fractional general manager roles in two Perth businesses and works on site in Perth on a quarterly rhythm with weekly or fortnightly catch-ups in between. Perth pricing is the same as everywhere else; travel time is absorbed in the price, and flights and accommodation are arranged by the client or charged at cost.",
    },
  ],
  "/": [
    {
      q: "Is a fractional COO an employee or a contractor?",
      a: "Fractional COOs are usually engaged as a business under a services agreement rather than employed, so there is no payroll, super, leave or recruitment fee. Ajility engages through AJILITY PTY LTD on a signed agreement, with fees quoted plus GST.",
    },
    {
      q: "Should I use a fractional COO or an outsourced operations agency?",
      a: "An agency usually supplies a team and its own processes to run a function for you. A fractional COO is one senior operator inside your leadership team who builds your capability and hands it back. With Ajility you work with the principal directly.",
    },
  ],
  "/about": [
    {
      q: "Are fractional COOs only for startups?",
      a: "No. Fractional COOs are most useful in established, founder-led businesses that have outgrown informal management. Ajility works with businesses typically doing $3M to $30M with 15 to 150 staff, where growth has outpaced structure.",
    },
    {
      q: "Can a fractional COO help get my business ready to sell?",
      a: "Yes, on the operating side. Buyers pay more attention to a business that runs without its founder, with clean reporting, documented processes and clear accountability, and a fractional COO builds that. Valuation, tax and legal advice on the sale sit with your accountant and lawyer.",
    },
    {
      q: "How do I prepare a business for sale in Australia?",
      a: "Start two to three years out with your accountant and lawyer on structure, tax and valuation. Alongside that, make the operation diligence-ready: leaders who run it without you, reliable monthly numbers and documented processes. Ajility does the operating work only.",
    },
    {
      q: "How do I prepare my business for succession to the next generation?",
      a: "Separate ownership succession, which needs your lawyer and accountant, from operating succession. The second means defining the seats, giving the successor clear accountabilities and a scorecard, and running a leadership rhythm that does not depend on the outgoing owner.",
    },
  ],
  "/about/adam-ayoub": [
    {
      q: "What experience should a fractional COO have?",
      a: "There is no licence for the role, so judge on track record: years holding P&L responsibility in general manager or COO seats, experience at your size and complexity, and references from founders. Ajility's principal has more than fifteen years in GM and COO roles.",
    },
  ],
  "/advisers": [
    {
      q: "My accountant says my business relies too much on me. Who can actually fix that?",
      a: "An operator rather than an adviser. Ajility works inside the business on structure, people and execution, keeps your accountant central and, with your permission, shares the diagnostic findings with them. It takes no accounting, tax, legal or lending work.",
    },
    {
      q: "I'm an accountant and my client has no management reporting rhythm. Who can install one?",
      a: "An operator who works inside the business and leaves the books with you. Ajility installs the meeting and reporting rhythm, takes no accounting or tax work and, with the client's permission, shares the diagnostic findings and roadmap with you.",
    },
    {
      q: "My client's business has no clear roles or structure. Who can help them?",
      a: "Ajility designs the structure, accountability chart and role scorecards inside the business, keeps the referring adviser central and, with the client's permission, shares findings with you. It takes no accounting, tax, legal or lending work.",
    },
    {
      q: "My client keeps missing their business plan targets. As their accountant, who can I refer them to?",
      a: "Where the cause is execution rather than the numbers, introduce an operator. Ajility installs the plan, owners and weekly rhythm, takes no accounting or tax work, and with client permission shares findings and the roadmap with you.",
    },
    {
      q: "Do operations consultants pay referral fees to accountants?",
      a: "Some do, and any such arrangement should be disclosed to the client. Most advisers simply want their client looked after. Ajility keeps the referring adviser central and, with the client's permission, shares findings with them.",
    },
    {
      q: "I'm a business broker. How do I get a client's operations ready for sale?",
      a: "Buyers discount businesses that depend on the founder. Before listing, document how the business runs, install a leadership rhythm and a scorecard with real actuals, and make roles clear. Ajility does that operational work; valuation and legal matters stay with you and the lawyers.",
    },
  ],
  "/decision-sprint": [
    {
      q: "Should I hire a COO or a general manager?",
      a: "A general manager usually runs the business day to day with P&L ownership as the founder steps back; a COO runs operations beside a founder who stays as CEO. Decide which seats the founder keeps first. Ajility can define the role before you recruit.",
    },
    {
      q: "Should I bring in a business partner or hire a COO?",
      a: "A partner shares ownership and risk; a COO shares the operating load. If you need capital or a co-owner's commitment, look for a partner and take legal advice. If what you need is execution, a fractional COO provides it without changing ownership.",
    },
    {
      q: "Should I hire a COO or promote someone internally?",
      a: "Promote when an internal leader already has the team's respect and has shown the judgement the seat needs; hire when the gap is capability nobody has. Define the seat and its scorecard first either way. Ajility's Decision Sprint scores this exact question.",
    },
    {
      q: "How do I write a business case for replacing our core system?",
      a: "State the problem in operating terms, the options including doing nothing, total cost over the system's life, measurable benefits with owners, and the risks. Ajility's Decision Sprint is built for buy, build or replace questions like this one.",
    },
    {
      q: "Should I open a second site or wait?",
      a: "Test it as a decision, not a feeling: can the first site run without you, does the second site's case hold on conservative numbers, and is there cash for a slow start? Score opening, waiting and partnering against agreed criteria before committing.",
    },
  ],
  "/fractional-coo-central-coast": [
    {
      q: "Do I need a fractional COO or an operations manager?",
      a: "An operations manager runs daily delivery; a COO designs and holds the operating system, the leadership rhythm and the numbers. If the system exists and needs running, hire the manager. If it does not exist yet, build it first, then recruit into a defined seat.",
    },
  ],
  "/fractional-coo-melbourne": [
    {
      q: "How much does an on-site operational review of a factory cost?",
      a: `Ajility's on-site Full Diagnostic and 90-Day Roadmap is ${faqPrice("Full Diagnostic and 90-Day Roadmap")} for one entity and one site, including a floor walk, a read of the data and a prioritised roadmap with owners and dates. Multi-site manufacturers are priced on scope.`,
    },
    {
      q: "What should I do when our manufacturing quotes aren't recovering our costs?",
      a: "Rebuild the workshop or line rate from current labour, overhead and input costs, recover setup and changeover time in every quote, and compare quoted hours with actual hours job by job. Old rates quietly subsidise customers once costs have moved.",
    },
    {
      q: "What is cost to serve in a wholesale distribution business?",
      a: "Cost to serve allocates freight, picking, handling, credit terms and service effort to each customer, showing who is profitable below the gross margin line. Reprice, change terms or set minimum orders for customers who cost more to serve than they earn.",
    },
  ],
  "/fractional-coo-newcastle": [
    {
      q: "How do I set the workshop rate for an engineering fabrication business?",
      a: "Divide the workshop's full cost, including labour, overhead and equipment, by realistic productive hours rather than paid hours, then add margin. Rebuild it whenever input costs move, and check quote-to-actual variance job by job.",
    },
  ],
  "/fractional-coo-perth": [
    {
      q: "How much do contractors lose by not claiming variations?",
      a: "There is no reliable industry figure. On thin-margin contracts, variations that are never claimed, or claimed after the notice period, can absorb much of a job's margin. A standing unclaimed variations line on the scorecard shows your exposure.",
    },
    {
      q: "What is a quote to actual report?",
      a: "A quote to actual report compares, job by job, the hours, materials and margin you quoted with what was actually spent. It shows which estimates are reliable and where your rates no longer cover cost.",
    },
    {
      q: "Why is cash so tight when our order book is full?",
      a: "Usually because cost is paid before claims are: mobilisation, wages, materials and unclaimed variations all fund the client's work. Late claims, retentions and slow certification widen the gap. A 13 week cash forecast tied to the claim schedule shows it early.",
    },
    {
      q: "How do I get paid for scope creep on a mining services contract?",
      a: "Recognise the change when it happens, notify within the contract's notice period, price it and seek approval before delivering. A variation register with a named owner and a weekly review stops you funding scope changes; disputes go to your lawyer.",
    },
    {
      q: "How do mobilisation costs eat into contract margin?",
      a: "Travel, inductions, equipment transport, accommodation and setup are paid before the first claim and are often under recovered in the rates. Track mobilisation cost against what the contract pays for it, by contract, and price the next one from actuals.",
    },
  ],
  "/fractional-coo-sydney": [
    {
      q: "How do I fix low utilisation in a professional services firm?",
      a: "Measure billable hours against available hours by person each week, track scope changes and write-offs by job, and price on realistic estimates. Many firms find margin they had already earned but lost between delivery and invoice.",
    },
    {
      q: "How do consulting firms stop scope creep eating their margins?",
      a: "Agree scope in writing, log every change when it is requested, price it before the work is done and review job-level margin weekly. A simple scope-change discipline usually recovers several points of margin that were already earned.",
    },
  ],
  "/how-we-work": [
    {
      q: "How do I know if a fractional COO is actually working?",
      a: "Judge it at the 90-day mark: are priorities clear and owned, does the leadership meeting run to time, does the scorecard carry real actuals, and has the founder stopped being the switchboard? Ajility reviews progress in writing at each boundary.",
    },
  ],
  "/packages": [
    {
      q: "What questions should I ask a fractional COO before signing?",
      a: "Ask what they will deliver in the first 90 days, who does the work, how they price, how you exit, what they hand over, and which operating roles they have held themselves. A credible operator answers all of it in writing before you sign.",
    },
    {
      q: "Do fractional COOs offer a money-back guarantee?",
      a: "Few do, and be wary of guarantees on business outcomes. Ajility limits your risk differently: published prices, quarterly commitment and, on the 90-Day Operating System Install, a Week 3 exit where you keep the plan and owe only the Full Diagnostic fee.",
    },
    {
      q: "What does a Full Diagnostic and 90-Day Roadmap deliver at the end?",
      a: `A prioritised 90-day roadmap with owners and dates, built from time on site, your numbers and conversations with leaders and staff. It is ${faqPrice("Full Diagnostic and 90-Day Roadmap")} for one entity and one site, over three to four weeks.`,
    },
    {
      q: "What would $5,000 a month get me from a fractional COO?",
      a: `With Ajility, the fortnightly rhythm base is ${faqPrice("Fortnightly rhythm base")}: every second week the leadership meeting is run, the numbers reviewed with your team and commitments held, with a quarterly plan reset. Actively led service lines are added on top.`,
    },
    {
      q: "What does around $10,000 a month buy in operational leadership?",
      a: `At that level an Ajility retainer can typically combine the weekly rhythm base, ${faqPrice("Weekly rhythm base")}, with one or more actively led service lines, ${faqPrice("Each additional active service line")} each. The exact mix is scoped to your business.`,
    },
  ],
  "/scan": [
    {
      q: "What happens on the call after the Operating Scan?",
      a: "Ajility walks you through the score, the weakest areas and what usually sits behind them, and tells you plainly whether a paid diagnostic is worth it or whether you can fix it yourself.",
    },
    {
      q: "Is a free business health check any good?",
      a: "A free check is useful for finding where to look, not for deciding what to fix. Ajility's Operating Scan is free, takes about six minutes and names your weakest areas; treat it as a screening result, not an audit.",
    },
    {
      q: "Should I do the free scan or the paid diagnostic first?",
      a: `Take the free scan first if you want a quick direction in six minutes. Go straight to the Operating Diagnostic, ${faqPrice("Operating Diagnostic")}, when the result matters commercially and you need evidence rather than self-report.`,
    },
    {
      q: "What questions are in a business health check?",
      a: "Good health checks use plain statements you can recognise about direction, leadership, people, meeting rhythm, numbers and systems, answered on a scale. Ajility's Operating Scan uses 21 such statements, answered from Not us to Always.",
    },
    {
      q: "How is the Operating Scan scored?",
      a: "Each of the 21 statements is answered on a scale from Not us to Always. The answers roll up into a provisional overall score with the weakest areas named. It is provisional because it rests on your answers, not on evidence Ajility has examined.",
    },
    {
      q: "What can a business diagnostic not tell me?",
      a: "A diagnostic shows where the business is constrained and what to fix first; it does not fix it, and it is not an audit of accounts, tax or legal compliance. Those sit with your accountant and lawyer, and Ajility stays on the operational side.",
    },
    {
      q: "Is there a free business operations health check for Australian businesses?",
      a: "Yes. Ajility's Operating Scan is a free operations health check built for founder-led Australian businesses of roughly $3M to $30M. It takes about six minutes, scores six areas and names the weakest, and you see the result without giving an email address.",
    },
    {
      q: "What does a business operations health check cover?",
      a: "Six areas: Direction, Leadership, People, Rhythm, Numbers and Systems. Each is read through plain statements about how the business runs week to week, so the result points at where to look first rather than at a generic maturity score.",
    },
    {
      q: "Who is the free operations health check for?",
      a: "Owners and general managers of established, founder-led businesses, usually between $3M and $30M in revenue with 15 to 150 staff. It is not built for start-ups or for businesses already running a full leadership rhythm, who tend to score well and learn little from it.",
    },
  ],
  "/tools": [
    {
      q: "What free tools does Ajility offer?",
      a: "Ajility offers six free calculators and tools: the Cash Runway Calculator, the Price Change Impact Calculator, the Time and Accountability Grid, the Fractional COO vs Full Time COO Cost Calculator, the Revenue Leakage Self Check and the Weekly Scorecard Builder, alongside the free six minute Operating Scan.",
    },
    {
      q: "Should I build our business systems myself or hire help?",
      a: "Start yourself: list the decisions that keep reaching you, set a fixed weekly leadership meeting and pick a few measures to review in it. Bring in help when commitments keep slipping or nobody owns the numbers. Ajility's free Operating Scan shows where to start.",
    },
    {
      q: "Are free government business advisory programs enough, or do I need a COO?",
      a: "Government programs offer subsidised advisory sessions and are worth using for direction and referrals, but they do not put an operator inside your leadership meeting. Start with free options, including Ajility's six-minute Operating Scan, then decide what level of help fits.",
    },
    {
      q: "Is there a business health check designed for board reporting?",
      a: "For boards, a health check should show where the leadership team agrees and where it does not. Ajility's Leadership Survey asks the owners and senior managers 60 statements individually, inside an engagement, and produces a leadership report and a group report showing where the leadership team disagrees with itself and with the floor.",
    },
    {
      q: "How often should a business do a health check?",
      a: "A light check each quarter, when the plan resets, catches drift early; a deeper evidence-based review suits once a year or before a major change such as a sale, raise or expansion. Re-running the same check shows whether areas are actually improving.",
    },
    {
      q: "What is an operating maturity assessment?",
      a: "It rates how established a business's practices are, from informal to embedded, across areas such as planning, accountability, reporting and systems. Ajility takes an Operating Maturity baseline at the start of an install and re-scores it at handover.",
    },
    {
      q: "What does the Leadership Survey cover?",
      a: "The Leadership Survey is the full bank of 60 statements, answered individually by the owners and senior managers inside an engagement. It produces the leadership report and the group report, which shows where the leadership team disagrees with itself and with the floor. It is used within an engagement rather than as a free tool.",
    },
  ],
};

export const homeFaqs: PageFaq[] = [
  {
    q: "What is a fractional COO and how does Ajility work?",
    a: "A fractional COO is an experienced operational leader who works with your business part-time, embedding with your leadership team to install structure, clarify priorities and drive execution. Ajility acts as a hands-on operator, not an advisor who writes reports and leaves.",
  },
  {
    q: "Who is Ajility for?",
    a: "Ajility is built for founder-led businesses that have outgrown informal management. If your team is reactive, ownership is unclear, or growth is creating friction, Ajility helps you build controlled, measurable and sustainable momentum.",
  },
  {
    q: "What services does Ajility provide?",
    a: "Ajility offers six service lines: operating system design; people, culture and accountability; strategy-to-execution support; performance and visibility reporting; project and initiative leadership; and operational excellence improvement.",
  },
  {
    q: "How is Ajility different from a management consultant?",
    a: "Consultants typically diagnose and recommend. Ajility implements. We work alongside founders and leadership teams to build cadences, accountability and scorecards that keep running after we leave.",
  },
  {
    q: "What happens in the first 90 days?",
    a: "Ajility works with the leadership team to set clear priorities, install execution discipline and build operational visibility. At the 90-day boundary progress is reviewed against those priorities.",
  },
  {
    q: "Which industries does Ajility work with?",
    a: "Ajility has led operational work across healthcare, professional services, transport and logistics, FMCG, mining, manufacturing, construction, retail, trade, recruitment and workplace technology.",
  },
  {
    q: "Where does Ajility operate?",
    a: "Ajility supports businesses across Australia, with a focus on Perth, Melbourne, Sydney, the Central Coast and Newcastle.",
  },
  ...pageFaqAdditions["/"],
];
